import { create } from 'zustand';

/**
 * アプリ全体で使用する「今日」の日付を管理
 *
 * - 通常: 実際の今日を返す
 * - デバッグモード: 設定された日付を返す（UIテスト用）
 *
 * ⚠️ 重要: アプリ内で「今日」を取得する際は、new Date() を直接使わず、
 * 必ず useCurrentDate() または getCurrentDate() を使用すること。
 */
interface CurrentDateStore {
  /** デバッグ用に上書きされた日付（null = 実際の今日を使用） */
  overrideDate: Date | null;

  /** 「今日」を取得（上書きがあればそれを、なければ実際の今日を返す） */
  getCurrentDate: () => Date;

  /** デバッグ用: 日付を上書き */
  setOverrideDate: (date: Date | null) => void;

  /** デバッグ用: 実際の今日にリセット */
  resetToRealToday: () => void;
}

/**
 * 現在の日付を管理するZustandストア
 *
 * 通常は実際の今日を返し、デバッグモードでは任意の日付に上書き可能。
 */
export const useCurrentDateStore = create<CurrentDateStore>((set, get) => ({
  overrideDate: null,

  getCurrentDate: () => {
    const { overrideDate } = get();
    if (overrideDate) {
      return overrideDate;
    }
    // 実際の今日を返す（時刻は0:00:00.000にリセット）
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  },

  setOverrideDate: (date: Date | null) => {
    if (date) {
      // 時刻を0:00:00.000にリセット
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      set({ overrideDate: normalized });
    } else {
      set({ overrideDate: null });
    }
  },

  resetToRealToday: () => {
    set({ overrideDate: null });
  },
}));
